import {
  rem,
  radius,
  spacing,
  token,
  TmElement,
} from '@thiememeulenhoff/lit-sdk';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { tagName } from '../../helpers';
import { LoaderVariant } from './loader.types';

const tag = tagName('loader');

const iconSize = 16;

const loaderStyles = css`
  :host {
    --spinner-size: ${rem(iconSize)};
    --spinner-animation-name: spinner;
    --spinner-indicator-color: ${token('loader-spinner-indicator-default')};
    --spinner-track-color: ${token('loader-spinner-track-default')};
    --spinner-track-width: ${rem(Math.round(iconSize / 8))};

    align-items: center;
    color: ${token('loader-foreground-muted')};
    display: inline-flex;
    gap: ${spacing(200)};
    justify-content: flex-start;
  }

  .loader {
    animation-duration: 1s;
    animation-iteration-count: infinite;
    animation-name: var(--spinner-animation-name);
    animation-timing-function: ease, step-start, cubic-bezier(0.1, 0.7, 1, 0.1);
    block-size: var(--spinner-size);
    border-block-end-color: var(--spinner-track-color);
    border-block-start-color: var(--spinner-indicator-color);
    border-inline-color: var(--spinner-track-color);
    border-radius: ${radius('extra-large')};
    border-style: solid;
    border-width: var(--spinner-track-width);
    inline-size: var(--spinner-size);
  }

  @keyframes spinner {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  :host([variant='page']) {
    display: flex;
    flex-direction: column;
    inline-size: ${rem(200)};
    position: relative;
  }

  :host([variant='page']) :where(.loader) {
    inset-block-start: ${rem(48)};
    inset-inline-start: ${rem(88)};
    position: absolute;
  }
`;

@customElement(tag)
export class LoaderComponent extends TmElement {
  static override styles = loaderStyles;

  @property({ type: String, reflect: true })
  variant: LoaderVariant = 'default';

  pageLoader() {
    if (this.variant === 'page') {
      return html`
        <svg
          id="Layer_2"
          data-name="Layer 2"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 445 279.08"
        >
          <defs>
            <style>
              .cls-1 {
                fill: #e5f4fb;
              }

              .cls-1,
              .cls-2 {
                fill-rule: evenodd;
              }

              .cls-1,
              .cls-2,
              .cls-3,
              .cls-4 {
                stroke-width: 0px;
              }

              .cls-2 {
                fill: #f4f0e8;
              }

              .cls-3 {
                fill: #0070a5;
              }

              .cls-4 {
                fill: #0090d4;
              }
            </style>
          </defs>
          <g id="Layer_1-2" data-name="Layer 1">
            <g>
              <path
                class="cls-2"
                d="M33,53.88c0,.7,0,1.5.1,2.2l1.3,19.4h0l10.7,166.6h355l15.9-209.3c1.4-18.5-14-34-32.6-32.7L61.3,23.28c-16.1,1.1-28.4,14.6-28.3,30.6Z"
              />
              <path
                class="cls-3"
                d="M385.58,9c6.04,0,11.65,2.46,15.78,6.91,4.11,4.43,6.12,10.18,5.66,16.18l-15.27,200.98H53.54l-10.16-158.2-1.3-19.4-.02-.34-.04-.29c-.02-.19-.02-.66-.02-.97v-.06c-.07-11.32,8.68-20.8,19.95-21.57L384.03,9.05c.52-.04,1.04-.05,1.55-.05M385.58,0c-.72,0-1.45.03-2.18.08L61.3,23.28c-16.1,1.1-28.4,14.6-28.3,30.6,0,.7,0,1.5.1,2.2l1.3,19.4,10.7,166.6h355l15.9-209.3C417.34,15,403.18,0,385.58,0h0Z"
              />
            </g>
            <path
              class="cls-3"
              d="M0,243.08h445l-3.9,13.7c-3.8,13.2-15.8,22.3-29.5,22.3H33.4c-13.7,0-25.7-9.1-29.5-22.3l-3.9-13.7Z"
            />
            <path class="cls-4" d="M271,243.08h-97v12h97v-12Z" />
            <path
              class="cls-1"
              d="M224,131.08c1.1,0,2,.9,2,2v.2c0,1.1-.9,2-2,2s-2-.9-2-2v-.2c0-1.1.9-2,2-2ZM224,138.88c1.1,0,2,.9,2,2v.5c0,1.1-.9,2-2,2s-2-.9-2-2v-.5c0-1.1.9-2,2-2ZM224,146.88c1.1,0,2,.9,2,2v.5c0,1.1-.9,2-2,2s-2-.9-2-2v-.5c0-1.1.9-2,2-2ZM224,154.88c1.1,0,2,.9,2,2v.2c0,1.1-.9,2-2,2s-2-.9-2-2v-.2c0-1.1.9-2,2-2Z"
            />
          </g>
        </svg>
        <div class="loader"></div>
        <slot></slot>
      `;
    }
    return html``;
  }

  override render() {
    return html`${this.pageLoader()}
      <div class="loader"></div>
      <slot></slot>`;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'a-loader': LoaderComponent;
  }
}
